import { useEffect, useMemo, useState } from "react";
import { Play, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";

const PINK = "#EE2677";

function formatElapsed(startedAt) {
  if (!startedAt) return "";
  const start = new Date(startedAt).getTime();
  if (!Number.isFinite(start)) return "";
  const mins = Math.max(0, Math.floor((Date.now() - start) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Mandatory Start bar — top of every KA task screen.
 * Staff must tap Start before Done; timing feeds KA learning.
 */
export default function ProductionStageStartBar({
  info,
  role,
  done = false,
  busy = false,
  onStart,
  gateMessage = "",
}) {
  const started = Boolean(info?.is_stage_started || info?.stage_started_at);
  const startedLabel = info?.stage_started_label_ist || "";
  const worker = info?.stage_worker || info?.assigned_staff || "";
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    if (!started || done) return undefined;
    const tick = () => setElapsed(formatElapsed(info?.stage_started_at));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [started, done, info?.stage_started_at]);

  const roleWord = useMemo(
    () => ({ baker: "Baking", froster: "Frosting", decorator: "Decoration" }[role] || "Job"),
    [role]
  );

  if (done) return null;

  if (started) {
    return (
      <div
        className="rounded-2xl border border-emerald-300 bg-emerald-50 px-3 py-3 shadow-sm"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <Timer size={18} className="text-emerald-700" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-emerald-900">
              {roleWord} started
              {worker ? ` · ${worker}` : ""}
            </p>
            <p className="text-xs font-semibold text-emerald-800/80">
              {startedLabel ? `Since ${startedLabel}` : "Timer running"}
              {elapsed ? ` · ${elapsed}` : ""}
            </p>
          </div>
          <span className="rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white">
            Live
          </span>
        </div>
      </div>
    );
  }

  const blocked = Boolean(gateMessage);

  return (
    <div className="rounded-2xl border border-[#EBB3A9] bg-white p-3 shadow-sm">
      <p className="mb-2 text-center text-xs font-bold text-[#666]">
        Tap <span className="text-[#EE2677]">Start</span> when you begin — required before Done
      </p>
      {blocked ? (
        <div className="mb-2 rounded-xl bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-900">
          {gateMessage}
        </div>
      ) : null}
      <Button
        type="button"
        disabled={busy || blocked}
        onClick={onStart}
        className="h-14 w-full rounded-full text-lg font-extrabold text-white hover:opacity-95"
        style={{ background: blocked ? "#9CA3AF" : PINK }}
      >
        {busy ? (
          "Starting..."
        ) : (
          <>
            <Play size={20} className="mr-2 fill-current" aria-hidden />
            Start {roleWord}
          </>
        )}
      </Button>
    </div>
  );
}
